import { CommonModule } from '@angular/common';
import { Component, Input, NgModule, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ActionLinkModule } from '@app/components/actions/action-link/action-link.component';
import { LinkModule } from '@app/directives/link.directive';
import { DriveItem } from '@microsoft/microsoft-graph-types';
import { of, Subscription } from 'rxjs';
import { concatMap, switchMap } from 'rxjs/operators';
import { OneDriveService } from '../../../../services/onedrive.service';
import { UploadDocumentModalComponent, UploadDocumentModalModule } from '../upload-document-modal/upload-document-modal.component';
import { UploadDocumentWarningMessageComponent, UploadDocumentWarningMessageModule } from '../upload-document-warning-message/upload-document-warning-message.component';

@Component({
  selector: 'app-upload-document-button',
  templateUrl: './upload-document-button.component.html',
  styleUrls: ['./upload-document-button.component.scss']
})
export class UploadDocumentButtonComponent implements OnInit, OnDestroy {
  @Input() onedriveLink: string;
  uploadedFile: DriveItem;
  private subscription: Subscription = new Subscription();

  private dialogRef: MatDialogRef<UploadDocumentModalComponent>;

  constructor(public dialog: MatDialog, private onedriveService: OneDriveService) { }

  ngOnInit() {
  }

  uploadFile(e: Event) {
    const target: HTMLInputElement = e.target as HTMLInputElement;
    const file = target.files[0];
    const reader = new FileReader();
    const fileSizeLimit = 4194304;

    // reset input field
    target.value = '';

    if (!file) {
      return;
    }

    // check filesize - max limit is 4MB
    if (file.size > fileSizeLimit) {
      const warningDialogRef = this.dialog.open(UploadDocumentWarningMessageComponent, {});
      warningDialogRef.componentInstance.onedriveLink = this.onedriveLink;
    } else {
      reader.onload = () => {
        const rawData = reader.result;

        // open dialog
        this.dialogRef = this.dialog.open(UploadDocumentModalComponent, {});

        // listens for cancel and override event from dialog in case the user wants to stop/replace the upload
        this.subscription.add(
          this.dialogRef.componentInstance.emitData
            .pipe(switchMap(event => {
              if (event.cancel) {
                return of(null);
              } else if (event.override) {
                return this.onedriveService.uploadFile(file.name, rawData);
              } else {
                return this.onedriveService.getFile(file.name).pipe(concatMap(res => {
                  // Document already exists in one drive
                  if (res && res.id) {
                    return of('doc exists');
                  } else if (res === 404) {
                    return this.onedriveService.uploadFile(file.name, rawData);
                  } else {
                    return of('error');
                  }
                }));
              }
            }))
            .subscribe(result => {
              if (result && result !== 'doc exists' && result !== 'error') {
                this.uploadedFile = result;
                this.dialogRef.componentInstance.fileUrl = this.uploadedFile.webUrl;
                this.dialogRef.componentInstance.uploading = false;
                this.dialogRef.componentInstance.docExists = false;
              } else if (result === 'doc exists') {
                this.dialogRef.componentInstance.docExists = true;
              } else {
                console.log('Error status' + result);
                this.dialogRef.componentInstance.docExists = false;
                this.dialogRef.componentInstance.uploading = true;
                this.dialogRef.componentInstance.error = true;
              }
            }));
      };

      reader.readAsArrayBuffer(file);
    }
  }

  ngOnDestroy() {
    this.subscription && this.subscription.unsubscribe();
  }
}
@NgModule({
  declarations: [UploadDocumentButtonComponent],
  exports: [UploadDocumentButtonComponent],
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    ActionLinkModule,
    UploadDocumentModalModule,
    LinkModule,
    UploadDocumentWarningMessageModule,
  ]
})
export class UploadDocumentButtonModule { }
